import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getToken } from '../lib/api';
import { fmtMoney, getLang, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { addDaysToKey, COACH_TZ, fmtLongDay, fmtTime, toCoachDateKey } from '../lib/time';
import { BookForSheet } from './BookForSheet';
import { MoveSheet } from './MoveSheet';

/**
 * Calendar → «Неделя» (server: routes/week.ts): the week as a grid, Monday
 * to Sunday, one block per session, its height its length.
 *
 * A tap on a block opens that day with the session's card; a tap on an
 * empty time starts «Записать» at that time; holding a block lets it be
 * dragged to another time, which is «Перенести» with its usual sheet.
 *
 * One request per week. The weeks either side are fetched behind it, so
 * ‹ › and a swipe show them at once.
 */

export interface WeekSession {
  id: string; startsAt: string; endsAt: string; kind: string;
  names: string[]; people: number;
  state: 'paid' | 'collect' | 'noShow';
  owed: number; moveBookingId: string | null;
}
export interface Week {
  from: string; startHour: number; endHour: number;
  sessions: WeekSession[];
  work: [string, string][]; blocks: [string, string][]; hot: string[];
  count: number; toCollect: number;
}

const HOUR = 46;
const SNAP = 30;
/** How long a block is held before it lifts for dragging. */
export const HOLD_MS = 450;

/** The Monday of the week holding a 'YYYY-MM-DD' day. */
export const mondayOf = (key: string) => addDaysToKey(key, -((new Date(`${key}T12:00:00Z`).getUTCDay() + 6) % 7));

/** Minutes since midnight, in the coach's time zone. */
function minutesOf(iso: string | number) {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: COACH_TZ, hour: '2-digit', minute: '2-digit', hour12: false })
    .formatToParts(new Date(iso));
  const h = Number(p.find((x) => x.type === 'hour')!.value) % 24;
  return h * 60 + Number(p.find((x) => x.type === 'minute')!.value);
}

/** A day and a time of day in the coach's time zone, as an instant. */
function instantAt(key: string, minutes: number) {
  const [y, m, d] = key.split('-').map(Number);
  const wall = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  let t = wall;
  for (let i = 0; i < 2; i++) {
    const [ky, km, kd] = toCoachDateKey(new Date(t)).split('-').map(Number);
    const shown = Date.UTC(ky, km - 1, kd) + minutesOf(t) * 60000;
    t += wall - shown;
  }
  return new Date(t).toISOString();
}

/** A span cut to one day: its minutes on that day, or null. */
function onDay(key: string, s: string, e: string): [number, number] | null {
  const sk = toCoachDateKey(new Date(s)), ek = toCoachDateKey(new Date(new Date(e).getTime() - 1));
  if (key < sk || key > ek) return null;
  const m: [number, number] = [key === sk ? minutesOf(s) : 0, key === ek ? (minutesOf(e) || 24 * 60) : 24 * 60];
  return m[1] > m[0] ? m : null;
}

// Fetched weeks, kept while the Calendar is open in this app session.
const cache = new Map<string, Week>();
const inflight = new Map<string, Promise<Week>>();

export function WeekView({ fetchWeek, initialDay, onOpenSession, headerAction }: {
  fetchWeek: (from: string) => Promise<Week>;
  initialDay: string;
  onOpenSession: (day: string, sessionId: string) => void;
  /** Beside the arrows, for the week on screen (its Monday): «Поделиться окнами». */
  headerAction?: (from: string) => ReactNode;
}) {
  const t = useT();
  const loc = getLang() === 'ru' ? 'ru-RU' : 'en-GB';
  const todayKey = toCoachDateKey(new Date());
  const [from, setFrom] = useState(() => mondayOf(initialDay));
  const [week, setWeek] = useState<Week | null>(null);
  const [now, setNow] = useState(Date.now());
  const [booking, setBooking] = useState<string | null>(null);
  const [bookFor, setBookFor] = useState<{ at: string; id: string; name: string } | null>(null);
  const [moving, setMoving] = useState<{ s: WeekSession; to: string } | null>(null);
  const [drag, setDrag] = useState<{ s: WeekSession; day: number; min: number } | null>(null);

  const get = useCallback((f: string) => {
    if (cache.has(f)) return Promise.resolve(cache.get(f)!);
    if (inflight.has(f)) return inflight.get(f)!;
    const p = fetchWeek(f).then((w) => { cache.set(f, w); return w; }).finally(() => inflight.delete(f));
    inflight.set(f, p);
    return p;
  }, [fetchWeek]);

  /** A week shown from the cache, or fetched; then the weeks either side fetched behind it. */
  const show = useCallback((f: string) => {
    let live = true;
    setWeek(cache.get(f) ?? null);
    get(f).then((w) => { if (live) setWeek(w); }).catch(() => { if (live) setWeek(null); })
      .finally(() => { void get(addDaysToKey(f, -7)).catch(() => {}); void get(addDaysToKey(f, 7)).catch(() => {}); });
    return () => { live = false; };
  }, [get]);
  // Opened again (perhaps after changes in the day view): fetched afresh.
  useState(() => cache.clear());
  useEffect(() => show(from), [from, show]);
  /** After a booking or a move from here: every cached week may be out of date. */
  const reload = () => { cache.clear(); show(from); };

  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(i); }, []);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysToKey(from, i)), [from]);
  // The grid's hours, stretched for a session outside them.
  const [lo, hi] = useMemo(() => {
    let a = week?.startHour ?? 7, b = week?.endHour ?? 22;
    for (const s of week?.sessions ?? []) {
      a = Math.min(a, Math.floor(minutesOf(s.startsAt) / 60));
      b = Math.max(b, Math.ceil((minutesOf(s.startsAt) + (new Date(s.endsAt).getTime() - new Date(s.startsAt).getTime()) / 60000) / 60));
    }
    return [a, Math.min(24, b)];
  }, [week]);
  const y = (min: number) => ((min - lo * 60) / 60) * HOUR;

  // ---- label: «Неделя 12–18 окт» ----
  const f = (key: string, o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(loc, { timeZone: 'UTC', ...o }).format(new Date(`${key}T12:00:00Z`)).replace(/\.$/, '');
  const last = days[6];
  const range = from.slice(0, 7) === last.slice(0, 7)
    ? `${f(from, { day: 'numeric' })}–${f(last, { day: 'numeric', month: 'short' })}`
    : `${f(from, { day: 'numeric', month: 'short' })} – ${f(last, { day: 'numeric', month: 'short' })}`;

  // ---- swipe between weeks ----
  const grid = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const step = (n: number) => { haptic.tap(); setFrom(addDaysToKey(from, 7 * n)); };

  // ---- long-press and drag ----
  const press = useRef<{ timer: number; x: number; y: number; s: WeekSession } | null>(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;
  const suppressClick = useRef(false);
  const cellAt = (cx: number, cy: number) => {
    const el = grid.current?.querySelector('.wk__cols') as HTMLElement | null;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const day = Math.floor(((cx - r.left) / r.width) * 7);
    if (day < 0 || day > 6) return null;
    const min = Math.round(((cy - r.top) / HOUR * 60 + lo * 60) / SNAP) * SNAP;
    return { day, min: Math.max(lo * 60, Math.min(hi * 60 - SNAP, min)) };
  };
  useEffect(() => {
    // While a block is held, the page does not scroll under the finger.
    const stop = (e: TouchEvent) => { if (dragRef.current) e.preventDefault(); };
    document.addEventListener('touchmove', stop, { passive: false });
    return () => document.removeEventListener('touchmove', stop);
  }, []);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = press.current;
      if (!p) return;
      if (!dragRef.current) {
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 8) { clearTimeout(p.timer); press.current = null; }
        return;
      }
      const c = cellAt(e.clientX, e.clientY);
      if (c) setDrag({ s: p.s, ...c });
    };
    const up = () => {
      const p = press.current;
      if (!p) return;
      clearTimeout(p.timer);
      press.current = null;
      const d = dragRef.current;
      if (!d) return;
      setDrag(null);
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 400);
      const to = instantAt(days[d.day], d.min);
      if (new Date(to).getTime() === new Date(d.s.startsAt).getTime()) return;
      if (new Date(to).getTime() <= Date.now()) { haptic.error(); return; }
      setMoving({ s: d.s, to });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [days, lo, hi]);

  const label = (s: WeekSession) => (s.kind === 'group' || s.people >= 3)
    ? t('wk.group', { n: s.people }) : s.names.join(' · ');
  const nowMin = minutesOf(now);
  const hours = Array.from({ length: hi - lo }, (_, i) => lo + i);

  return (
    <div className="wk">
      <div className="wk__head">
        <div className="wk__title">
          <b>{t('wk.title', { range })}</b>
          <span>
            {week ? [
              `${week.count} ${t.word('co.today.sessions', week.count)}`,
              week.toCollect > 0 ? t('wk.toCollect', { amount: fmtMoney(Math.round(week.toCollect)) }) : '',
            ].filter(Boolean).join(' · ') : ' '}
          </span>
        </div>
        <div className="wk__nav">
          {headerAction?.(from)}
          <button className="wk__step" aria-label={t('wk.prev')} onClick={() => step(-1)}>‹</button>
          <button className="wk__today" disabled={from === mondayOf(todayKey)}
            onClick={() => { haptic.tap(); setFrom(mondayOf(todayKey)); }}>{t('wk.today')}</button>
          <button className="wk__step" aria-label={t('wk.next')} onClick={() => step(1)}>›</button>
        </div>
      </div>

      <div className="wk__grid" ref={grid}
        onTouchStart={(e) => { swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        onTouchEnd={(e) => {
          const s0 = swipe.current; swipe.current = null;
          if (!s0 || dragRef.current || press.current) return;
          const dx = e.changedTouches[0].clientX - s0.x, dy = e.changedTouches[0].clientY - s0.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > 1.5 * Math.abs(dy)) step(dx < 0 ? 1 : -1);
        }}>
        <div className="wk__days">
          <span />
          {days.map((k) => (
            <span key={k} className={`wk__day${k === todayKey ? ' wk__day--today' : ''}`}>
              <small>{f(k, { weekday: 'short' })}</small><b>{f(k, { day: 'numeric' })}</b>
            </span>
          ))}
        </div>
        <div className="wk__body" style={{ height: (hi - lo) * HOUR }}>
          <div className="wk__hours">
            {hours.map((h) => <span key={h} style={{ top: y(h * 60) }}>{String(h).padStart(2, '0')}:00</span>)}
          </div>
          <div className={`wk__cols${week ? '' : ' wk__cols--loading'}`}>
            {hours.map((h) => <i key={h} className="wk__line" style={{ top: y(h * 60) }} />)}
            {days.map((k, di) => {
              const past = k < todayKey;
              return (
                <div key={k} className={`wk__col${k === todayKey ? ' wk__col--today' : ''}${past ? ' wk__col--past' : ''}`}
                  onClick={(e) => {
                    if (suppressClick.current || !week) return;
                    if ((e.target as HTMLElement).closest('.wk__blk')) return;
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const min = lo * 60 + Math.floor(((e.clientY - r.top) / HOUR) * 60 / SNAP) * SNAP;
                    const at = instantAt(k, min);
                    if (new Date(at).getTime() <= Date.now()) return;
                    const t0 = new Date(at).getTime();
                    if (week.blocks.some(([s, e2]) => t0 >= new Date(s).getTime() && t0 < new Date(e2).getTime())) return;
                    haptic.tap();
                    setBooking(at);
                  }}>
                  {week?.work.map(([s, e], i) => {
                    const m = onDay(k, s, e);
                    return m && <i key={`w${i}`} className="wk__work" style={{ top: y(Math.max(m[0], lo * 60)), height: y(Math.min(m[1], hi * 60)) - y(Math.max(m[0], lo * 60)) }} />;
                  })}
                  {week?.blocks.map(([s, e], i) => {
                    const m = onDay(k, s, e);
                    if (!m || m[1] <= lo * 60 || m[0] >= hi * 60) return null;
                    return <i key={`b${i}`} className="wk__block" style={{ top: y(Math.max(m[0], lo * 60)), height: y(Math.min(m[1], hi * 60)) - y(Math.max(m[0], lo * 60)) }} />;
                  })}
                  {week?.hot.filter((h) => toCoachDateKey(new Date(h)) === k).map((h) => (
                    <span key={h} className="wk__hot" style={{ top: y(minutesOf(h)) }} aria-label={`🔥 ${fmtTime(h)}`}>🔥</span>
                  ))}
                  {week?.sessions.filter((s) => toCoachDateKey(new Date(s.startsAt)) === k).map((s) => {
                    const top = y(minutesOf(s.startsAt));
                    const h = ((new Date(s.endsAt).getTime() - new Date(s.startsAt).getTime()) / 3600000) * HOUR;
                    const done = new Date(s.endsAt).getTime() < now;
                    return (
                      <button key={s.id} className={`wk__blk wk__blk--${s.state}${done ? ' wk__blk--done' : ''}${drag?.s.id === s.id ? ' wk__blk--lifted' : ''}`}
                        style={{ top: top + 1, height: Math.max(h - 2, 16) }}
                        aria-label={`${fmtTime(s.startsAt)} ${label(s)}`}
                        onPointerDown={(e) => {
                          if (!s.moveBookingId) return;
                          const p = { x: e.clientX, y: e.clientY, s, timer: 0 };
                          p.timer = window.setTimeout(() => {
                            haptic.press();
                            setDrag({ s, day: di, min: minutesOf(s.startsAt) });
                          }, HOLD_MS);
                          press.current = p;
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                        onClick={() => {
                          if (suppressClick.current) return;
                          haptic.tap();
                          onOpenSession(k, s.id);
                        }}>
                        {/* Names only — the hour axis gives the time. A pair is a name to a
                            line; otherwise the label wraps, and is cut only past what fits. */}
                        <span className="wk__name" lang={getLang()}
                          style={{ WebkitLineClamp: Math.max(1, Math.floor((h - 6) / 12)) }}>
                          {s.people === 2 && s.kind !== 'group'
                            ? s.names.map((n, i) => <span key={i}>{i > 0 && <br />}{n}</span>)
                            : label(s)}
                        </span>
                      </button>
                    );
                  })}
                  {drag && drag.day === di && (
                    <span className="wk__ghost" style={{ top: y(drag.min) + 1,
                      height: ((new Date(drag.s.endsAt).getTime() - new Date(drag.s.startsAt).getTime()) / 3600000) * HOUR - 2 }}>
                      <small>{fmtTime(instantAt(k, drag.min))}</small>
                    </span>
                  )}
                  {k === todayKey && nowMin >= lo * 60 && nowMin < hi * 60 && (
                    <i className="wk__now" style={{ top: y(nowMin) }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="wk__hint">{t('wk.hint')}</div>

      {booking && (
        <WhoSheet at={booking} onClose={() => setBooking(null)}
          onPick={(c) => { setBooking(null); setBookFor({ at: booking, id: c.id, name: c.name }); }} />
      )}
      {bookFor && (
        <BookForSheet clientId={bookFor.id} name={bookFor.name} at={bookFor.at}
          onClose={() => { setBookFor(null); reload(); }} onDone={reload} />
      )}
      {moving && (
        <MoveSheet booking={{ booking_id: moving.s.moveBookingId, starts_at: moving.s.startsAt }}
          name={label(moving.s)} isSeries={false} to={moving.to}
          onClose={() => { setMoving(null); reload(); }} onDone={reload} />
      )}
    </div>
  );
}

/** «Записать» from an empty time: who, then the usual sheet at that time. */
function WhoSheet({ at, onClose, onPick }: {
  at: string; onClose: () => void; onPick: (c: { id: string; name: string }) => void;
}) {
  const t = useT();
  const [clients, setClients] = useState<any[] | null>(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/coach/insights`, {
      headers: { authorization: `Bearer ${getToken()}` },
    }).then((r) => r.json()).then((r) => setClients(r.clients ?? [])).catch(() => setClients([]));
  }, []);
  const shown = (clients ?? [])
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 12);
  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('wk.bookWho', { when: `${fmtLongDay(new Date(at))}, ${fmtTime(at)}` })}</div>
        <input className="paybar__opt" style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
          placeholder={t('co.rate.searchClient')} value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <div style={{ maxHeight: '40vh', overflowY: 'auto', marginTop: 8 }}>
          {clients === null ? <div className="skeleton" /> :
           shown.length === 0 ? <div className="card__when">{t('co.add.nobody')}</div> :
           shown.map((c) => (
            <button key={c.id} className="daysheet__row"
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, borderBottom: '1px solid var(--line-soft)' }}
              onClick={() => { haptic.tap(); onPick({ id: c.id, name: c.name }); }}>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span className="card__when">{c.credits > 0 ? t('co.add.credits', { n: c.credits }) : ''}</span>
            </button>
          ))}
        </div>
        <button className="paybar__opt" style={{ width: '100%', marginTop: 12 }} onClick={onClose}>{t('co.pkg.back')}</button>
      </div>
    </div>,
    document.body,
  );
}
